/* global Highcharts:false */
import { getLeadingTradedContract, hasLastTradePrice } from '@/utils/helpers';
import api from '@/services/api';
import constants from '@/constants';
import marketChangePrice from '@/components/market/market-change-price/market-change-price.vue';

const PINCH_HINT_STORAGE_KEY = 'cms-map-widget-pinch-hint-dismissed';
const DEFAULT_EMPTY_COLOR = '#d9d9d9';
const MAP_WIDGET_CONTEXT_MENU_ITEMS = [
  'viewFullscreen',
  'printChart',
  'separator',
  'downloadPNG',
  'downloadJPEG',
  'downloadSVG',
  'downloadPDF',
  'separator',
  'downloadCSV',
  'downloadXLS',
  'separator',
  'viewMapDataTable',
];
const DEFAULT_CONTEXT_MENU_ITEMS = MAP_WIDGET_CONTEXT_MENU_ITEMS.filter(item => item !== 'viewMapDataTable');
const RENDERER_TYPE_ELECTION_ELECTORAL = 'electionelectoral';
const RENDERER_TYPE_ELECTION_SENATE = 'electionsenate';
const CONTEXT_MENU_OVERFLOW_CLASS = 'cms-map-widget-menu-overflow-open';
const CONTEXT_MENU_VIEWPORT_PADDING = 12;
const CONTEXT_MENU_BOTTOM_UI_SELECTORS = [
  '.app-privacy-statement',
  '.app-layout__bottom-nav',
];
const PARTY_DEMOCRATIC = 'democratic';
const PARTY_REPUBLICAN = 'republican';
const WIDTH_PERCENTAGE_THRESHOLD = 5;
const PRICE_FACTORS = {
  LOW: 0.64,
  MEDIUM: 0.79,
};
const DEFAULT_PALETTE = {
  gray: [
    { from: 0, to: PRICE_FACTORS.LOW, color: '#dddddd' },
    { from: PRICE_FACTORS.LOW, to: PRICE_FACTORS.MEDIUM, color: '#bbbbbb' },
    { from: PRICE_FACTORS.MEDIUM, to: 1, color: '#888888' },
  ],
  democratic: [
    { from: 0, to: PRICE_FACTORS.LOW, color: '#8aafff' },
    { from: PRICE_FACTORS.LOW, to: PRICE_FACTORS.MEDIUM, color: '#5c80dc' },
    { from: PRICE_FACTORS.MEDIUM, to: 1, color: '#244999' },
  ],
  republican: [
    { from: 0, to: PRICE_FACTORS.LOW, color: '#ff8b98' },
    { from: PRICE_FACTORS.LOW, to: PRICE_FACTORS.MEDIUM, color: '#ff5865' },
    { from: PRICE_FACTORS.MEDIUM, to: 1, color: '#d22532' },
  ],
};
const DENSE_STATE_LABELS = [
  'VT', 'NH', 'MA', 'RI', 'CT', 'NJ', 'DE', 'MD', 'DC',
];

const roundToTwo = value => Math.round(value * 100) / 100;
const HTML_ESCAPE_CHARACTERS = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};
const SAFE_TOOLTIP_COLOR = /^#[0-9a-f]{6}$/i;

function escapeTooltipHtml(value) {
  return String(value === null || typeof value === 'undefined' ? '' : value)
    .replace(/[&<>"']/g, character => HTML_ESCAPE_CHARACTERS[character]);
}

function getSafeTooltipImageUrl(value) {
  if (typeof value !== 'string' || !value.trim()) {
    return '';
  }

  try {
    const baseUrl = typeof window !== 'undefined' && window.location
      ? window.location.href
      : 'https://www.predictit.org/';
    const imageUrl = new URL(value.trim(), baseUrl);
    const isHttps = imageUrl.protocol === 'https:';
    const isSameOriginHttp = imageUrl.protocol === 'http:' &&
      typeof window !== 'undefined' && imageUrl.origin === window.location.origin;
    return isHttps || isSameOriginHttp ? imageUrl.href : '';
  } catch (error) {
    return '';
  }
}

function getSafeTooltipColor(value) {
  return typeof value === 'string' && SAFE_TOOLTIP_COLOR.test(value)
    ? value
    : DEFAULT_EMPTY_COLOR;
}

export default {
  components: {
    marketChangePrice,
  },
  props: {
    mapName: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      isLoading: true,
      hasError: false,
      mapData: [],
      legendData: [],
      chart: null,
      showPinchHint: false,
      showDataTable: false,
      isContextMenuOpen: false,
      shouldShowPinchHint: false,
      interactionDismissHandler: null,
      contextMenuObserver: null,
      contextMenuOverflowContainers: [],
    };
  },
  computed: {
    mapConfig() {
      return (this.mapData && this.mapData.length > 0) ? this.mapData[0] : {};
    },
    rendererType() {
      return ((this.mapConfig && this.mapConfig.rendererType) || '').toLowerCase();
    },
    isElectionMap() {
      return this.rendererType === RENDERER_TYPE_ELECTION_ELECTORAL ||
        this.rendererType === RENDERER_TYPE_ELECTION_SENATE;
    },
    isSenateMap() {
      return this.rendererType === RENDERER_TYPE_ELECTION_SENATE;
    },
    mapTitle() {
      return (this.mapConfig && this.mapConfig.title) || '';
    },
    classModifiers() {
      return {
        'cms-map-widget--menu-open': this.isContextMenuOpen,
        'cms-map-widget--election': this.isElectionMap,
        'cms-map-widget--with-overview': this.showOverviewPanel,
        'cms-map-widget--with-legend': this.showLegend,
      };
    },
    showLegend() {
      const defaultValue = !this.isElectionMap || !this.showOverviewPanel;
      return this.getMapFlag('showLegend', defaultValue) && this.legendData.length > 0;
    },
    canShowDataTable() {
      return this.getMapFlag('showDataTable', true);
    },
    showElectionToolbar() {
      return this.isElectionMap && this.getMapFlag('showToolbar', true) && this.toolbarSegments.length > 0;
    },
    showMajorityLabel() {
      return this.isElectionMap && this.getMapFlag('showMajorityLabel', false);
    },
    showOverviewPanel() {
      return this.isElectionMap && this.getMapFlag('showOverview', true) && this.overviewContracts.length > 0;
    },
    overviewTitleDesktop() {
      if (this.mapConfig && this.mapConfig.overviewTitle) {
        return this.mapConfig.overviewTitle;
      }

      return this.isSenateMap ? 'Control of the Senate' : 'Presidential Election Winner?';
    },
    overviewTitleMobile() {
      if (this.mapConfig && (this.mapConfig.overviewMobileTitle || this.mapConfig.overviewTitle)) {
        return this.mapConfig.overviewMobileTitle || this.mapConfig.overviewTitle;
      }

      return this.isSenateMap ? 'Control of the Senate' : 'Who will win the presidential election?';
    },
    overviewContracts() {
      const itemWithOverview = (this.mapData || []).find(item =>
        Array.isArray(item.overviewContracts) && item.overviewContracts.length > 0,
      );
      return itemWithOverview ? itemWithOverview.overviewContracts.slice(0, 2) : [];
    },
    predefinedGroups() {
      const groups = (this.mapConfig && this.mapConfig.predefinedGroups) || [];
      return Array.isArray(groups) ? groups : [];
    },
    toolbarBaselines() {
      const baselines = (this.mapConfig && this.mapConfig.toolbarBaselines) || [];
      return Array.isArray(baselines) ? baselines : [];
    },
    electionRows() {
      const groupedByStateCode = this.groupLocationsByStateCode();
      return Object.keys(groupedByStateCode).reduce((rows, stateCode) => {
        const predefinedGroup = this.getPredefinedGroupForState(stateCode);
        if (predefinedGroup && predefinedGroup.isLocked) {
          return rows;
        }

        const representative = this.getRepresentativeLocation(groupedByStateCode[stateCode]);
        const leadingContract = this.getLeadingContract(representative);
        rows.push({
          stateCode,
          locationData: representative,
          leadingContract,
          party: this.getContractParty(leadingContract),
          stateValue: this.getStateValue(stateCode, representative),
        });
        return rows;
      }, []);
    },
    democraticToolbarCounter() {
      return this.buildToolbarCounter(PARTY_DEMOCRATIC);
    },
    republicanToolbarCounter() {
      return this.buildToolbarCounter(PARTY_REPUBLICAN);
    },
    toolbarTotalWidth() {
      const total = this.democraticToolbarCounter.widthTotal +
        this.republicanToolbarCounter.widthTotal;
      return total > 0 ? total : 1;
    },
    majorityLabel() {
      if (this.mapConfig && this.mapConfig.majorityLabel) {
        return this.mapConfig.majorityLabel;
      }

      if (this.mapConfig && this.mapConfig.majorityTarget) {
        return this.isSenateMap ? `${this.mapConfig.majorityTarget} Seats` : `${this.mapConfig.majorityTarget}`;
      }

      return this.isSenateMap ? '50 Seats' : 'Majority';
    },
    toolbarSegments() {
      const democratic = this.democraticToolbarCounter;
      const republican = this.republicanToolbarCounter;
      const segments = [
        this.createToolbarSegment(democratic, 'predefined', PARTY_DEMOCRATIC, true),
        this.createToolbarSegment(democratic, 'high', PARTY_DEMOCRATIC),
        this.createToolbarSegment(democratic, 'medium', PARTY_DEMOCRATIC),
        this.createToolbarSegment(democratic, 'low', PARTY_DEMOCRATIC),
        this.createToolbarSegment(republican, 'low', PARTY_REPUBLICAN),
        this.createToolbarSegment(republican, 'medium', PARTY_REPUBLICAN),
        this.createToolbarSegment(republican, 'high', PARTY_REPUBLICAN),
        this.createToolbarSegment(republican, 'predefined', PARTY_REPUBLICAN, true),
      ];

      return segments.filter(segment => segment.value > 0 && segment.width > 0);
    },
    mapTableRows() {
      return (this.mapData || []).reduce((rows, item, locationIndex) => {
        const locationCode = this.normalizeLocationCode(item.locationCode);
        const markets = (Array.isArray(item.markets) && item.markets.length > 0)
          ? item.markets
          : [item];

        markets.forEach((market, marketIndex) => {
          const contracts = Array.isArray(market.contracts) ? market.contracts : [];
          if (contracts.length === 0) {
            rows.push({
              key: `${locationCode || 'location'}-${locationIndex}-${market.marketId || marketIndex}`,
              stateCode: locationCode,
              marketName: market.marketName || market.name || 'Market',
              contractName: '',
              lastTradePrice: null,
              status: market.status || item.status || '',
              color: item.overrideColor || '',
            });
            return;
          }

          contracts.forEach((contract, contractIndex) => {
            rows.push({
              key: `${locationCode || 'location'}-${locationIndex}-${market.marketId || marketIndex}-${contract.contractId || contractIndex}`,
              stateCode: locationCode,
              marketName: market.marketName || market.name || 'Market',
              contractName: contract.contractName || contract.name || 'Contract',
              lastTradePrice: hasLastTradePrice(contract) ? contract.lastTradePrice : null,
              status: market.status || item.status || '',
              color: contract.color || item.overrideColor || (this.isElectionMap ? this.resolveElectionColor(item) : ''),
            });
          });
        });

        return rows;
      }, []);
    },
  },
  methods: {
    toCents(value) {
      const normalized = Number(value);
      if (isNaN(normalized)) {
        return 0;
      }
      return Math.round(normalized * 100);
    },
    hasTradePrice(contract) {
      return hasLastTradePrice(contract);
    },
    dismissPinchHint() {
      this.showPinchHint = false;
      this.shouldShowPinchHint = false;
      try {
        localStorage.setItem(PINCH_HINT_STORAGE_KEY, 'true');
      } catch (e) {
        // localStorage can be unavailable in privacy-restricted browser contexts.
      }
    },
    toggleDataTable() {
      if (!this.canShowDataTable) {
        return;
      }

      this.showDataTable = !this.showDataTable;
    },
    hideDataTable() {
      this.showDataTable = false;
    },
    clearContextMenuOverflowContainers() {
      (this.contextMenuOverflowContainers || []).forEach((element) => {
        if (element && element.classList) {
          element.classList.remove(CONTEXT_MENU_OVERFLOW_CLASS);
        }
      });
      this.contextMenuOverflowContainers = [];
    },
    syncContextMenuOverflowContainers(isOpen) {
      this.clearContextMenuOverflowContainers();

      if (!isOpen || !this.$el || typeof window === 'undefined') {
        return;
      }

      let element = this.$el.parentElement;
      while (element && element !== document.body) {
        const style = window.getComputedStyle(element);
        if (style.overflowX !== 'visible' || style.overflowY !== 'visible') {
          element.classList.add(CONTEXT_MENU_OVERFLOW_CLASS);
          this.contextMenuOverflowContainers.push(element);
        }
        element = element.parentElement;
      }
    },
    positionContextMenu() {
      if (!this.$el || typeof window === 'undefined') {
        return;
      }

      const contextMenu = this.$el.querySelector('.highcharts-contextmenu');
      if (!contextMenu) {
        return;
      }

      const bounds = contextMenu.getBoundingClientRect();
      const currentTop = parseFloat(contextMenu.style.top || '0') || contextMenu.offsetTop || 0;
      const currentLeft = parseFloat(contextMenu.style.left || '0') || contextMenu.offsetLeft || 0;
      let maxBottom = window.innerHeight - CONTEXT_MENU_VIEWPORT_PADDING;
      const maxRight = window.innerWidth - CONTEXT_MENU_VIEWPORT_PADDING;
      let nextTop = currentTop;
      let nextLeft = currentLeft;

      CONTEXT_MENU_BOTTOM_UI_SELECTORS.forEach((selector) => {
        const element = document.querySelector(selector);
        if (!element) {
          return;
        }
        const style = window.getComputedStyle(element);
        const elementBounds = element.getBoundingClientRect();
        const isVisible = style.display !== 'none' &&
          style.visibility !== 'hidden' &&
          elementBounds.width > 0 &&
          elementBounds.height > 0;

        if (isVisible && elementBounds.bottom >= window.innerHeight - 1) {
          maxBottom = Math.min(maxBottom, elementBounds.top - CONTEXT_MENU_VIEWPORT_PADDING);
        }
      });

      if (bounds.bottom > maxBottom) {
        nextTop -= bounds.bottom - maxBottom;
      }
      if (bounds.top + (nextTop - currentTop) < CONTEXT_MENU_VIEWPORT_PADDING) {
        nextTop += CONTEXT_MENU_VIEWPORT_PADDING - (bounds.top + (nextTop - currentTop));
      }
      if (bounds.right > maxRight) {
        nextLeft -= bounds.right - maxRight;
      }
      if (bounds.left + (nextLeft - currentLeft) < CONTEXT_MENU_VIEWPORT_PADDING) {
        nextLeft += CONTEXT_MENU_VIEWPORT_PADDING - (bounds.left + (nextLeft - currentLeft));
      }

      contextMenu.style.top = `${Math.round(nextTop)}px`;
      contextMenu.style.left = `${Math.round(nextLeft)}px`;
      contextMenu.style.maxHeight = `${Math.max(180, Math.round(window.innerHeight - (CONTEXT_MENU_VIEWPORT_PADDING * 2)))}px`;
      contextMenu.style.overflowY = 'auto';
    },
    syncContextMenuState() {
      if (!this.$el || typeof window === 'undefined') {
        return;
      }

      const contextMenu = this.$el.querySelector('.highcharts-contextmenu');
      let isOpen = false;
      if (contextMenu) {
        const style = window.getComputedStyle(contextMenu);
        const bounds = contextMenu.getBoundingClientRect();
        isOpen = style.display !== 'none'
          && style.visibility !== 'hidden'
          && bounds.width > 0
          && bounds.height > 0;
      }

      if (this.isContextMenuOpen !== isOpen) {
        this.isContextMenuOpen = isOpen;
        this.syncContextMenuOverflowContainers(isOpen);
      }
      if (isOpen) {
        this.positionContextMenu();
      }
    },
    stopContextMenuObserver() {
      if (this.contextMenuObserver) {
        this.contextMenuObserver.disconnect();
        this.contextMenuObserver = null;
      }
      this.clearContextMenuOverflowContainers();
      this.isContextMenuOpen = false;
    },
    startContextMenuObserver() {
      this.stopContextMenuObserver();
      if (!this.$el || typeof MutationObserver === 'undefined') {
        return;
      }

      this.contextMenuObserver = new MutationObserver(() => this.syncContextMenuState());
      this.contextMenuObserver.observe(this.$el, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class'],
      });
      this.syncContextMenuState();
    },
    isMobileLayout() {
      if (typeof window === 'undefined' || typeof window.matchMedia === 'undefined') {
        return false;
      }
      return window.matchMedia('(max-width: 767px)').matches;
    },
    getMapFlag(fieldName, defaultValue) {
      if (!this.mapConfig || this.mapConfig[fieldName] === null || typeof this.mapConfig[fieldName] === 'undefined') {
        return defaultValue;
      }

      return this.mapConfig[fieldName] !== false;
    },
    normalizeLocationCode(code) {
      const normalized = (code || '').toUpperCase().replace(/-/g, '');
      if (!normalized) {
        return '';
      }

      if (normalized.length >= 2) {
        return normalized.substring(0, 2);
      }

      return normalized;
    },
    getContractsForLocation(marketData) {
      if (!marketData) {
        return [];
      }

      const linkedMarkets = Array.isArray(marketData.markets) ? marketData.markets : [];
      if (linkedMarkets.length > 0) {
        return linkedMarkets.reduce((contracts, market) =>
          contracts.concat(Array.isArray(market.contracts) ? market.contracts : []), [],
        );
      }

      return Array.isArray(marketData.contracts) ? marketData.contracts : [];
    },
    getLeadingContract(marketData) {
      const contracts = this.getContractsForLocation(marketData);
      return getLeadingTradedContract(contracts);
    },
    getContractName(contract) {
      return ((contract && (contract.contractName || contract.shortName || contract.name)) || '').toLowerCase();
    },
    getContractParty(contract) {
      const party = ((contract && contract.party) || '').toLowerCase();
      if (party.includes('dem')) {
        return PARTY_DEMOCRATIC;
      }
      if (party.includes('rep') || party.includes('gop')) {
        return PARTY_REPUBLICAN;
      }

      const name = this.getContractName(contract);
      if (name.includes('republican') || name.includes('trump') || name.includes('rubio')) {
        return PARTY_REPUBLICAN;
      }
      if (name.includes('democrat') || name.includes('biden') || name.includes('shapiro') || name.includes('whitmer') || name.includes('buttigieg')) {
        return PARTY_DEMOCRATIC;
      }

      return '';
    },
    getOverviewContractParty(item) {
      let configuredContract = null;
      (this.mapData || []).some((location) => {
        configuredContract = this.getContractsForLocation(location)
          .find(contract => Number(contract.contractId) === Number(item.contractId));
        return !!configuredContract;
      });

      return this.getContractParty(configuredContract || item);
    },
    getOverviewNameStyle(item) {
      return {
        backgroundColor: item.color || this.getPaletteColor(this.getOverviewContractParty(item) || 'gray', 1),
      };
    },
    getRepresentativeLocation(locations) {
      if (!Array.isArray(locations) || locations.length === 0) {
        return null;
      }

      const sorted = locations.slice().sort((a, b) => {
        const contractA = this.getLeadingContract(a);
        const contractB = this.getLeadingContract(b);
        const scoreA = contractA ? Number(contractA.lastTradePrice) || 0 : 0;
        const scoreB = contractB ? Number(contractB.lastTradePrice) || 0 : 0;
        return scoreB - scoreA;
      });

      return sorted[0];
    },
    getPaletteThresholds(party) {
      const customThresholds = (this.mapConfig && this.mapConfig.paletteThresholds) || [];
      const matchingThresholds = Array.isArray(customThresholds)
        ? customThresholds.filter(item => ((item.party || '').toLowerCase() === party))
        : [];

      if (matchingThresholds.length > 0) {
        return matchingThresholds;
      }

      return DEFAULT_PALETTE[party] || DEFAULT_PALETTE.gray;
    },
    getPaletteColor(party, price) {
      const normalizedParty = party || 'gray';
      const thresholds = this.getPaletteThresholds(normalizedParty);
      const normalizedPrice = Math.min(Math.max(Number(price) || 0, 0), 1);
      const match = thresholds.find((threshold) => {
        const from = typeof threshold.from === 'number' ? threshold.from : 0;
        const to = typeof threshold.to === 'number' ? threshold.to : 1;
        return normalizedPrice >= from && normalizedPrice <= to;
      });

      return (match && match.color) || thresholds[thresholds.length - 1].color;
    },
    getPaletteBucket(price) {
      const normalizedPrice = Math.min(Math.max(Number(price) || 0, 0), 1);
      if (normalizedPrice <= PRICE_FACTORS.LOW) {
        return 'low';
      }
      if (normalizedPrice <= PRICE_FACTORS.MEDIUM) {
        return 'medium';
      }
      return 'high';
    },
    resolveBaseColor(contract) {
      const party = this.getContractParty(contract);
      if (party === PARTY_REPUBLICAN) {
        return '#bc1500';
      }
      if (party === PARTY_DEMOCRATIC) {
        return '#2f88dc';
      }
      return '#9a9a9a';
    },
    applyGradient(color, contract, gradientSetting) {
      if (gradientSetting !== 2 || !contract) {
        return color;
      }

      const normalized = Math.min(Math.max(Number(contract.lastTradePrice) || 0, 0), 1);
      if (typeof color !== 'string' || !color.startsWith('#') || color.length !== 7) {
        return color;
      }

      const red = parseInt(color.substring(1, 3), 16);
      const green = parseInt(color.substring(3, 5), 16);
      const blue = parseInt(color.substring(5, 7), 16);
      const alpha = Math.max(0.35, Math.min(1, 0.35 + (normalized * 0.65)));

      return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    },
    resolveColor(locationData, emptyColor) {
      if (!locationData) {
        return emptyColor;
      }

      if (this.isElectionMap) {
        return this.resolveElectionColor(locationData);
      }

      if (locationData.overrideColor) {
        return locationData.overrideColor;
      }

      const leadingContract = this.getLeadingContract(locationData);
      const contractColor = (leadingContract && leadingContract.color) || '';
      const baseColor = contractColor || this.resolveBaseColor(leadingContract);

      return this.applyGradient(
        baseColor, leadingContract, Number(locationData.gradientSetting) || 0);
    },
    resolveElectionColor(locationData) {
      if (!locationData) {
        return this.getEmptyColor();
      }

      if (locationData.overrideColor) {
        return locationData.overrideColor;
      }

      const leadingContract = this.getLeadingContract(locationData);
      const contractColor = (leadingContract && leadingContract.color) || '';
      if (contractColor && (this.mapConfig.paletteMode || '').toLowerCase() !== 'partythreshold') {
        return contractColor;
      }

      const party = this.getContractParty(leadingContract);
      if (!party) {
        return this.getEmptyColor();
      }

      return this.getPaletteColor(party, leadingContract.lastTradePrice);
    },
    getEmptyColor() {
      const itemWithEmptyColor = (this.mapData || []).find(item => !!item.emptyColor);
      return itemWithEmptyColor ? itemWithEmptyColor.emptyColor : DEFAULT_EMPTY_COLOR;
    },
    groupLocationsByStateCode() {
      return (this.mapData || []).reduce((acc, item) => {
        const stateCode = this.normalizeLocationCode(item.locationCode);
        if (!stateCode) {
          return acc;
        }

        if (!acc[stateCode]) {
          acc[stateCode] = [];
        }
        acc[stateCode].push(item);
        return acc;
      }, {});
    },
    getPredefinedGroupForState(stateCode) {
      return this.predefinedGroups.find((group) => {
        const codes = Array.isArray(group.locationCodes) ? group.locationCodes : [];
        return codes.map(code => this.normalizeLocationCode(code)).includes(stateCode);
      }) || null;
    },
    getPredefinedGroupColor(group) {
      if (!group) {
        return this.getEmptyColor();
      }
      if (group.color) {
        return group.color;
      }

      const party = ((group.party || '').toLowerCase().includes('rep')) ? PARTY_REPUBLICAN : PARTY_DEMOCRATIC;
      return this.getPaletteColor(party, 1);
    },
    getStateName(stateCode, fallbackName) {
      return constants.statesCodeToNameMap[stateCode] || fallbackName || stateCode;
    },
    getStateValue(stateCode, locationData) {
      if (locationData && locationData.stateValue) {
        return Number(locationData.stateValue) || 1;
      }

      if (this.isSenateMap) {
        return 1;
      }

      return constants.statesCodeToElectoralVotesMap[stateCode] || 1;
    },
    getTooltipHtml(point) {
      if (this.isElectionMap) {
        return this.getElectionTooltipHtml(point);
      }

      const marketData = point.marketData;
      if (!marketData) {
        return `<div class="cms-map-widget__tooltip-empty">${escapeTooltipHtml(point.stateName)}</div>`;
      }

      const contracts = this.getContractsForLocation(marketData).slice(0, 2);
      const contractsHtml = contracts.map(contract =>
        `<div><strong>${escapeTooltipHtml(contract.contractName || contract.name || 'Contract')}</strong>: ${this.toCents(contract.lastTradePrice)}&cent;</div>`,
      ).join('');
      const marketName = escapeTooltipHtml(marketData.marketName || point.stateName);
      const stateName = escapeTooltipHtml(point.stateName);

      return `<div class="cms-map-widget__tooltip"><div class="cms-map-widget__tooltip-title">${stateName}</div><div>${marketName}</div>${contractsHtml}</div>`;
    },
    getElectionTooltipHtml(point) {
      const marketData = point.marketData;
      if (!marketData) {
        return false;
      }

      const stateCode = point.stateCode || '';
      const showSplitStateNote = !this.isSenateMap && (stateCode === 'ME' || stateCode === 'NE');
      let stateContext = ` (${point.stateValue || 0}${showSplitStateNote ? ' STATEWIDE' : ''})`;
      if (this.isSenateMap) {
        stateContext = stateCode === 'GA' ? ' (REGULAR)' : '';
      }

      const stateLabel = escapeTooltipHtml(`${point.stateName}${stateContext}`);
      const leadingContract = this.getLeadingContract(marketData);
      const color = getSafeTooltipColor(this.resolveElectionColor(marketData));
      const contracts = this.getContractsForLocation(marketData).slice(0, 2);
      const marketName = escapeTooltipHtml(marketData.marketName || point.stateName);
      const contractsHtml = contracts.map((contract, index) => {
        const imageUrl = getSafeTooltipImageUrl(contract.contractImageUrl);
        const image = imageUrl
          ? `<img class="cms-map-widget__tooltip-contract-image" src="${escapeTooltipHtml(imageUrl)}" alt="">`
          : '';
        const change = hasLastTradePrice(contract) ? this.getChangeCents(contract) : null;
        const changeHtml = change === null ? '' : `<span class="cms-map-widget__tooltip-change">${change > 0 ? '+' : ''}${change}&cent;</span>`;
        const price = hasLastTradePrice(contract) ? `${this.toCents(contract.lastTradePrice)}&cent;` : '-';
        const contractName = escapeTooltipHtml(contract.contractName || contract.name || 'Contract');
        return `<div class="cms-map-widget__tooltip-contract ${index === 0 ? 'cms-map-widget__tooltip-contract--primary' : ''}">${image}<div><div class="cms-map-widget__tooltip-contract-name">${contractName}</div><div><span class="cms-map-widget__tooltip-price">${price}</span>${changeHtml}</div></div></div>`;
      }).join('');
      const winner = leadingContract ? `<div class="cms-map-widget__tooltip-leader">${escapeTooltipHtml(leadingContract.contractName || leadingContract.name || 'Leading contract')}</div>` : '';
      const note = showSplitStateNote
        ? `<div class="cms-map-widget__tooltip-note">See separate markets for individual<br>congressional districts in ${escapeTooltipHtml(this.getStateName(stateCode, point.stateName))}.</div>`
        : '';

      return `<div class="cms-map-widget__tooltip cms-map-widget__tooltip--election" style="border-color:${color}"><div class="cms-map-widget__tooltip-title" style="background:${color}">${stateLabel}</div><div class="cms-map-widget__tooltip-body"><div>${marketName}</div>${winner}${contractsHtml}${note}</div></div>`;
    },
    getChangeCents(contract) {
      if (!contract || contract.lastClosePrice === null || typeof contract.lastClosePrice === 'undefined') {
        return null;
      }

      return this.toCents(Number(contract.lastTradePrice) - Number(contract.lastClosePrice));
    },
    getPointMarketId(point) {
      const marketData = point.marketData;
      if (!marketData) {
        return null;
      }

      const interactionType = Number(marketData.interactionType) || 0;
      const linkedMarkets = Array.isArray(marketData.markets) ? marketData.markets : [];
      if (interactionType === 2 && linkedMarkets.length > 0) {
        return linkedMarkets[0].marketId || null;
      }

      return marketData.marketId || null;
    },
    getDataLabel(point) {
      if (!this.isElectionMap) {
        return point.stateCode || '';
      }

      const leadingContract = this.getLeadingContract(point.marketData);
      if (leadingContract) {
        return `${this.toCents(leadingContract.lastTradePrice)}¢`;
      }

      if (this.getContractsForLocation(point.marketData).length > 0) {
        return '-';
      }

      return DENSE_STATE_LABELS.includes(point.stateCode) ? point.stateCode : '';
    },
    getChartData(mapShapes) {
      const groupedByStateCode = this.groupLocationsByStateCode();
      const emptyColor = this.getEmptyColor();

      return mapShapes.map((shape) => {
        const stateCode = (shape.properties['hc-a2'] || '').toUpperCase();
        shape.stateName = (shape.name || '').toUpperCase();
        const locations = groupedByStateCode[stateCode] || [];
        const representative = this.getRepresentativeLocation(locations);
        const configuredGroup = this.getPredefinedGroupForState(stateCode);
        const predefinedGroup = configuredGroup && (configuredGroup.isLocked || !representative)
          ? configuredGroup
          : null;
        const marketData = predefinedGroup ? null : representative;
        return {
          stateName: shape.stateName,
          stateCode,
          stateValue: this.getStateValue(stateCode, marketData),
          marketData,
          predefinedGroup,
          color: predefinedGroup
            ? this.getPredefinedGroupColor(predefinedGroup)
            : this.resolveColor(marketData, emptyColor),
          value: marketData || predefinedGroup ? 1 : 0,
        };
      });
    },
    buildToolbarCounter(party) {
      const counter = {
        total: 0,
        widthTotal: 0,
        low: 0,
        medium: 0,
        high: 0,
        predefined: 0,
        lowWidth: 0,
        mediumWidth: 0,
        highWidth: 0,
        predefinedWidth: 0,
        predefinedLabel: 'No election',
        predefinedDisplayText: '',
      };

      this.toolbarBaselines
        .filter(baseline => this.getGroupParty(baseline) === party)
        .forEach((baseline) => {
          const requestedBucket = (baseline.bucket || '').toLowerCase();
          const bucket = ['low', 'medium', 'high'].includes(requestedBucket) ? requestedBucket : 'high';
          const count = Number(baseline.count) || 0;
          const reservedCount = baseline.reservedCount === null || typeof baseline.reservedCount === 'undefined'
            ? count
            : Number(baseline.reservedCount) || 0;
          counter[bucket] += count;
          counter[`${bucket}Width`] += reservedCount;
          counter.total += count;
          counter.widthTotal += reservedCount;
        });

      this.electionRows
        .filter(row => row.party === party)
        .forEach((row) => {
          const bucket = this.getPaletteBucket(row.leadingContract.lastTradePrice);
          const value = row.stateValue || 1;
          counter[bucket] += value;
          counter[`${bucket}Width`] += value;
          counter.total += value;
          counter.widthTotal += value;
        });

      this.predefinedGroups
        .filter(group => this.getGroupParty(group) === party)
        .forEach((group) => {
          const fallbackCount = Array.isArray(group.locationCodes) ? group.locationCodes.length : 0;
          const seatCount = group.seatCount === null || typeof group.seatCount === 'undefined'
            ? fallbackCount
            : Number(group.seatCount) || 0;
          const reservedSeatCount = group.reservedSeatCount === null || typeof group.reservedSeatCount === 'undefined'
            ? seatCount
            : Number(group.reservedSeatCount) || 0;
          counter.predefined += seatCount;
          counter.predefinedWidth += reservedSeatCount;
          counter.total += seatCount;
          counter.widthTotal += reservedSeatCount;
          if (group.label) {
            counter.predefinedLabel = group.label;
          }
          if (group.displayText) {
            counter.predefinedDisplayText = group.displayText;
          }
        });

      if (!counter.predefinedDisplayText) {
        counter.predefinedDisplayText = `${counter.predefined}`;
      }

      return counter;
    },
    getGroupParty(group) {
      const party = ((group && group.party) || '').toLowerCase();
      if (party.includes('rep')) {
        return PARTY_REPUBLICAN;
      }
      return PARTY_DEMOCRATIC;
    },
    createToolbarSegment(counter, bucket, party, striped = false) {
      const value = counter[bucket] || 0;
      const widthValue = counter[`${bucket}Width`];
      const width = roundToTwo(((typeof widthValue === 'number' ? widthValue : value) / this.toolbarTotalWidth) * 100);
      let colorPrice = 1;
      if (bucket === 'low') {
        colorPrice = PRICE_FACTORS.LOW;
      } else if (bucket === 'medium') {
        colorPrice = PRICE_FACTORS.MEDIUM;
      }
      return {
        key: `${party}-${bucket}`,
        party,
        bucket,
        value,
        width,
        color: this.getPaletteColor(party, colorPrice),
        striped,
        label: striped ? counter.predefinedLabel : '',
        displayText: striped ? counter.predefinedDisplayText : `${value}`,
        showText: width >= WIDTH_PERCENTAGE_THRESHOLD,
      };
    },
    renderChart() {
      const mapKey = 'countries/us/custom/us-small';
      if (!this.$refs.chart ||
          typeof Highcharts === 'undefined' ||
          !Highcharts.maps ||
          !Highcharts.maps[mapKey]) {
        this.hasError = true;
        return;
      }

      const mapShapes = Highcharts.geojson(Highcharts.maps[mapKey]);
      const data = this.getChartData(mapShapes);
      const vm = this;

      if (this.chart) {
        this.chart.destroy();
      }

      this.chart = Highcharts.mapChart(this.$refs.chart, {
        chart: {
          spacing: [0, 0, 0, 0],
          margin: [0, 0, 0, 0],
        },
        title: {
          text: '',
        },
        legend: {
          enabled: false,
        },
        credits: {
          enabled: false,
        },
        exporting: {
          enabled: this.getMapFlag('showExports', true),
          menuItemDefinitions: {
            viewMapDataTable: {
              text: 'View data table',
              onclick() {
                vm.toggleDataTable();
              },
            },
          },
          buttons: {
            contextButton: {
              menuItems: this.canShowDataTable
                ? MAP_WIDGET_CONTEXT_MENU_ITEMS
                : DEFAULT_CONTEXT_MENU_ITEMS,
            },
          },
        },
        mapNavigation: {
          enabled: true,
          enableButtons: false,
          enableDoubleClickZoomTo: true,
          enableTouchZoom: true,
          mouseWheelSensitivity: 1.1,
        },
        tooltip: {
          useHTML: true,
          borderRadius: 0,
          borderWidth: 0,
          shadow: false,
          backgroundColor: 'white',
          formatter: function formatter() {
            return vm.getTooltipHtml(this.point);
          },
        },
        plotOptions: {
          series: {
            cursor: 'pointer',
            point: {
              events: {
                click: function onClick() {
                  const marketId = vm.getPointMarketId(this);
                  if (marketId) {
                    window.location.href = `/markets/detail/${marketId}`;
                  }
                },
              },
            },
          },
        },
        series: [{
          mapData: mapShapes,
          data,
          joinBy: 'stateName',
          dataLabels: {
            enabled: true,
            formatter: function dataLabelFormatter() {
              return vm.getDataLabel(this.point);
            },
            useHTML: this.isElectionMap,
            style: {
              textOutline: 0,
              fontSize: this.isElectionMap ? '11px' : '10px',
              color: '#ffffff',
            },
          },
        }, {
          name: 'Separators',
          type: 'mapline',
          data: Highcharts.geojson(Highcharts.maps[mapKey], 'mapline'),
          color: '#c8c8c8',
          showInLegend: false,
          enableMouseTracking: false,
        }],
      });

      this.startContextMenuObserver();

      if (this.shouldShowPinchHint) {
        this.showPinchHint = true;
      }
    },
    loadMapData() {
      this.isLoading = true;
      this.hasError = false;
      this.showDataTable = false;

      return Promise.all([
        api.map.getMapData(this.mapName, false),
        api.map.getMapLegend(this.mapName, false),
      ])
        .then(([mapResponse, legendResponse]) => {
          this.mapData = Array.isArray(mapResponse.data) ? mapResponse.data : [];
          this.legendData = Array.isArray(legendResponse.data) ? legendResponse.data : [];
          return new Promise((resolve) => {
            this.$nextTick(() => {
              this.renderChart();
              resolve();
            });
          });
        })
        .catch(() => {
          this.hasError = true;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
  },
  mounted() {
    try {
      const dismissed = localStorage.getItem(PINCH_HINT_STORAGE_KEY) === 'true';
      this.shouldShowPinchHint = this.isMobileLayout() && !dismissed;
    } catch (e) {
      this.shouldShowPinchHint = this.isMobileLayout();
    }

    this.loadMapData();

    this.$nextTick(() => {
      if (this.$refs.chart) {
        this.interactionDismissHandler = () => this.dismissPinchHint();
        this.$refs.chart.addEventListener('touchstart', this.interactionDismissHandler, { passive: true });
        this.$refs.chart.addEventListener('mousedown', this.interactionDismissHandler);
        this.$refs.chart.addEventListener('wheel', this.interactionDismissHandler, { passive: true });
      }
    });
  },
  beforeDestroy() {
    this.stopContextMenuObserver();

    if (this.$refs.chart && this.interactionDismissHandler) {
      this.$refs.chart.removeEventListener('touchstart', this.interactionDismissHandler);
      this.$refs.chart.removeEventListener('mousedown', this.interactionDismissHandler);
      this.$refs.chart.removeEventListener('wheel', this.interactionDismissHandler);
    }

    if (this.chart) {
      this.chart.destroy();
      this.chart = null;
    }
  },
};



// WEBPACK FOOTER //
// ./src/components/cms/map-widget-view/map-widget-view.js